import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataContentProps, useUiAction } from '../../newron-core';
import { Instructions } from '../../content/instructions';
import { BoardInfo, BoardRow, ColumnConfig, NavigateAction, Sorting } from './types';
import { makeBillboardInfo } from './data';
import { Loading } from '../../design/loading/Loading';
import { getExportContainer } from '../../export';
import './billboard.css';
import { useCorePageState } from '../../page/provider';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { useMobile } from '../../context/mobile/mobile.provider';
import { shouldHideBillboard, syncSettingsOnBillboardKpiSelection } from './billboardUtils';
import { addTotalToMatchingSharedBrand } from '../utils';
import { navigation } from '@ecosystem/newron-design-system';

function Billboard({
  data,
  isLoading,
  instructions = {},
  componentSettings,
  componentName,
  collection,
  componentHeadingTitle,
}: DataContentProps) {
  const { perform } = useUiAction();
  const [prevDataStamp, setPrevDataStamp] = useState<number>();
  const [info, setInfo] = useState<BoardInfo>();
  const { dsl, globalWarnings } = useCorePageState();
  const exportRef = useRef<HTMLDivElement[]>([]);
  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];

  const allColumns = data?.getAllColumns() ?? [];

  const { Link } = navigation;

  useOnCollectionExportReady({
    collection,
    dsl,
    chartSliceRefs: exportRef,
    componentName,
    componentSettings,
    allColumns,
    componentHeadingTitle,
  });

  const moBillboardKpi = componentSettings?.moBillboardKpi;
  const moBillboardDataType = componentSettings?.moBillboardDataType;

  const [prevMoBillboardKpi, setPrevMoBillboardKpi] = useState(moBillboardKpi);

  const kpiName = moBillboardKpi?.length ? moBillboardKpi[0] : 'Unknown name';
  const kpiLabel = data?.getDataColumn(kpiName)?.label || 'Unknown label';

  const myInstructions = new Instructions(instructions, componentSettings);

  const grouping = myInstructions.getString('grouping');
  const highlight = myInstructions.getString('highlight');
  const highlightColumn = myInstructions.getString('highlightColumn');
  const highlightedBrands = myInstructions.getObject('highlightedBrands');
  const siblingBuffer = myInstructions.getNumber('siblingBuffer', 1);
  let title = myInstructions.getString('title');
  const billboardDataType = myInstructions.getString('brandsBillboardDataType', grouping);
  const limit = myInstructions.getNumber('limit', 10);
  const sortingInstructions = myInstructions.getObject('sorting') as Sorting;
  const asCard = myInstructions.getBoolean('asCard', true);
  const firstCol = myInstructions.getObject('firstColumn') as ColumnConfig;
  const secondCol = myInstructions.getObject('secondColumn') as ColumnConfig;
  let columns = myInstructions.getObject('columns', []) as ColumnConfig[];
  const isAllowedToDisplayEmptyState = myInstructions.getBoolean('isAllowedToDisplayEmptyState', false);
  const billboardNavigateAction = myInstructions.getObject('navigateConfig') as NavigateAction;

  const isNavigateLinkAccessible = billboardNavigateAction && moBillboardDataType?.[0] !== 'brand_owner_name';

  if (title) {
    title = myInstructions.getString('title').replace('[colName]', kpiLabel);
  }

  if (moBillboardKpi) {
    columns = columns.map((col: ColumnConfig) => {
      if (col.name === 'first_activity') {
        return col;
      }
      return {
        name: col.name.includes('change_') ? `change_${kpiName}` : kpiName,
        label: col.name.includes('change_') ? col.label : kpiLabel,
      };
    });
  }

  const sorting = {
    by: sortingInstructions?.by.includes('change_') ? `change_${kpiName}` : kpiName,
    direction: sortingInstructions?.direction || 'desc',
  } as Sorting;

  if (firstCol) {
    columns.push(firstCol);
  }
  if (secondCol) {
    columns.push(secondCol);
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (moBillboardKpi !== prevMoBillboardKpi) {
      setPrevMoBillboardKpi(moBillboardKpi);
      setPrevDataStamp(0);
    }
  }, [moBillboardKpi]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || data.stamp === prevDataStamp) {
      return;
    }

    const info = makeBillboardInfo(data, {
      title,
      grouping,
      highlight,
      highlightColumn,
      sorting,
      columnConfigs: columns,
      limit,
      siblingBuffer,
      highlightedBrands: highlightedBrands as string[],
      billboardDataType,
    });

    setInfo(info);
    setPrevDataStamp(data.stamp);
  }, [data, grouping, highlight, highlightColumn, title, limit, sorting, columns]);

  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  const getBillboard = (info: BoardInfo) => {
    const isWaved = info.outOfStackRows?.[0].rank !== limit + 1;
    return (
      <>
        {info.title && <div className="text-lg pb-2">{info.title}</div>}
        <table className="w-full">
          <thead>
            <tr>
              <th />
              {info.columns.map(col => (
                <th className="w-1 font-normal text-lighter text-sm pl-6 wrap" align="right" key={col}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {info.rows.length === 0 && (
              <tr>
                <td>
                  <span>No data available for your selected filters, try modifying your selections.</span>
                </td>
              </tr>
            )}
            {info.rows.map((row, idx) => (
              <BoardTableRow
                key={`${row.id}_${idx}`}
                row={row}
                isLast={idx === info.rows.length - 1}
                sharedBrands={billboardDataType === 'brand_name' ? sharedBrands : []}
              />
            ))}
            {info.outOfStackRows && (
              <tr className={` ${!isWaved ? 'border-b border-light' : ''}`}>
                <td colSpan={3}>
                  <span className={`${isWaved ? 'block wavy-separator' : ''} h-2 bg-dark`} />
                </td>
              </tr>
            )}
            {info.outOfStackRows?.map((row, idx) => (
              <BoardTableRow
                key={`${row.id}_${idx}`}
                row={row}
                isLast={idx === info.outOfStackRows.length - 1}
                sharedBrands={billboardDataType === 'brand_name' ? sharedBrands : []}
              />
            ))}
          </tbody>
        </table>
      </>
    );
  };

  const ExportContainer = useMemo(() => getExportContainer(componentName, 'inline'), []);

  if (collection && info && !info.rows.length) {
    collection.onExportReady(null);
    return isAllowedToDisplayEmptyState ? <EmptyState /> : <></>;
  }

  if (shouldHideBillboard(info)) {
    return null;
  }

  const handleActionClick = (actionConfig: NavigateAction) => {
    syncSettingsOnBillboardKpiSelection(moBillboardDataType, moBillboardKpi, actionConfig);

    perform(actionConfig.action);
  };

  return (
    <div className={`billboard flex-column flex-1 h-full relative min-w-72 ${asCard ? 'bg-white rounded-2 p-4' : ''}`}>
      <div>
        <Loading isActive={isLoading} withIndicator />
        {info && getBillboard(info)}
        <ExportContainer>
          <div
            className={`${componentName}-export-slide border border-light p-3 rounded w-2/6`}
            ref={el => (exportRef.current = [el])}
          >
            {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
            {info && getBillboard(info)}
          </div>
        </ExportContainer>
      </div>
      {!isLoading && isNavigateLinkAccessible && (
        <div className={'billboard-link'}>
          <Link onClick={() => handleActionClick(billboardNavigateAction)}>{billboardNavigateAction?.label}</Link>
        </div>
      )}
    </div>
  );
}

type RowProps = {
  row: BoardRow;
  isLast: boolean;
  sharedBrands: string[];
};
function BoardTableRow({ row, isLast, sharedBrands }: Readonly<RowProps>) {
  const { label, rank, columns, isHighlighted } = row;
  const { isMobile } = useMobile();

  return (
    <tr className={`${!isLast ? 'border-b border-light' : ''}`}>
      <td className="py-2">
        <span className={`flex flex-row gap-2 align-baseline ${isHighlighted ? 'font-bold' : ''}`}>
          <span>{rank}.</span>
          {/* added mobile-break-word class for > 758px screens */}
          <span className={`flex-1 ${isMobile ? ' mobile-break-word' : 'break-word'}`}>
            {addTotalToMatchingSharedBrand(label, label, sharedBrands)}
          </span>
        </span>
      </td>
      {columns.map((column, idx) => (
        <td key={`${row.id}_col${idx}`} align="right" className={`text-lg font-bold pl-6 py-2 wrap ${column.color}`}>
          {column.value}
        </td>
      ))}
    </tr>
  );
}

export { Billboard };
